<script setup>
import useDebug from "@/hooks/useDebug";
import { ref, computed, watch } from "vue";
import { useToast } from "vue-toastification";

import { monthDayYearFormat, yearMonthDayFormat } from "@/utils/dateUtils";

const props = defineProps({
  params: {
    type: Object,
    required: true,
  },
});

const { adjustLot, adjustExpiry } = useDebug();
const toast = useToast();

const dialog = ref(false);
const saving = ref(false);
const updatedLotData = ref({
  lot: "",
  expiry: "",
  qty: "",
  reason: "",
});

const field = computed(() => props.params.colDef.field);
const isLot = computed(() => field.value === 'lot');

const initialData = computed(() => {
  const { data, context } = props.params;
  const { items, pallet_qty, lot: lpLot, expiry: lpExpiry } = data;
  const productId = context.product?.data?.id;

  let lot, expiry, qty;

  if (items?.length > 1 && productId) {
  // if Lp has multiple items, we match the product and get the lot code or expiry from the first match
    const matchingItems = items.filter(item => item.product_id === productId);
    const firstMatchingItem = matchingItems[0];
    lot = firstMatchingItem?.lot_code;
    expiry = firstMatchingItem?.exp_date;
    qty = matchingItems.reduce((sum, item) => sum + item.qty, 0) || 0;
  } else {
  // if Lp has a single item, we use expiry and fallback to item.exp_date and we use lot and fallback to item.lot_code if it is not available
    const singleItem = items?.[0];
    lot = lpLot ?? singleItem?.lot_code;
    expiry = lpExpiry ?? singleItem?.exp_date;
    qty = singleItem?.qty;
  }

  if (expiry) {
    expiry = monthDayYearFormat(expiry) ?? expiry;
  }

  return {
    lot,
    expiry,
    qty: qty ?? pallet_qty,
  };
});

const displayValue = computed(() => {
  const value = isLot.value ? initialData.value.lot : initialData.value.expiry;
  return value ?? "NA";
});

watch(dialog, (isDialogVisible) => {
  if (isDialogVisible) {
    updatedLotData.value = {
      ...initialData.value,
      reason: "",
    };
  }
});

const saveUpdatedLot = async () => {
  saving.value = true;
  try {
    const { data, onFieldUpdated, api } = props.params;
    const lp_id = data.id;
    const { reason, lot, expiry } = updatedLotData.value;

    const apiCall = isLot.value ? adjustLot : adjustExpiry;

    const params = isLot.value
      ? {
          lot_code: lot,
          edit_reason: reason,
        }
      : {
          expiry_date: yearMonthDayFormat(expiry, '-') ?? expiry,
          edit_reason: reason,
        };

    const res = await apiCall(lp_id, params);

    if (res) {
      if (onFieldUpdated) {
        onFieldUpdated(data);
      }
      dialog.value = false;
      api.refreshServerSide({ purge: true });
    }
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div @click="dialog = true" class="cursor-pointer text-blue">{{ displayValue }}</div>
  <v-dialog v-model="dialog" max-width="600" persistent>
      <v-card
        :text="`LPN: ${props.params.data.number}`"
        title="Update Pallet"
      >
        <v-card-text>
          <div class="d-flex ga-4">
            <v-text-field 
              max-width="200" 
              variant="underlined" 
              hide-details="auto" 
              :label="isLot ? 'Lot' : 'Expiry'" 
              v-model="updatedLotData[field]">
            </v-text-field>
            <v-text-field 
              disabled
              max-width="200" 
              variant="underlined" 
              hide-details="auto" 
              label="Quantity" 
              v-model="updatedLotData.qty" 
              type="number">
            </v-text-field>
          </div>
          <v-text-field 
            v-model="updatedLotData.reason" 
            class="mb-4" 
            label="Reason for Edit" 
            variant="underlined" 
            hide-details="auto">
          </v-text-field>

          <small>LP ID: {{props.params.data.id}}</small>
        </v-card-text>
        <template v-slot:actions>
          <v-btn
            color="red"
            class="ml-auto"
            variant="text"
            @click="dialog = false"
          > 
            Cancel
          </v-btn>
          <v-btn
            color="green"
            variant="text"
            @click="saveUpdatedLot"
            :disabled="!updatedLotData.reason || !updatedLotData[field]"
            :loading="saving"
          >
            Save
          </v-btn>
        </template>
      </v-card>
  </v-dialog>
</template>
